import React, { useEffect, useState } from "react";
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { Button, Col, Row, Table } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getProducts } from "live-slices/thunk";
import * as XLSX from "xlsx";

interface FetchDataParams {
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
}

interface FetchDataResponse<T> {
  data: T[];
  totalPages: number;
}

interface PaginatedTableProps<T> {
  columns: ColumnDef<T, any>[];
  fetchData: (params: FetchDataParams) => Promise<any>;
  pageSize?: number;
  divClass?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
}

const PaginatedTable = ({
  columns,
  fetchData,
  pageSize = 10,
  divClass,
  tableClass,
  theadClass,
  trClass,
  thClass,
}: PaginatedTableProps<any>) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const [pageSizeState, setPageSizeState] = useState(pageSize);

  const dispatch = useDispatch<any>();

  const handleExport = async () => {
    try {
      const products = await dispatch(
        getProducts({ page: 1, count: 4000, status: "all", keyword: "" })
      ).unwrap();
      console.log("Fetched Products:", products);

      const exportData = products.data.map((item: any) => ({
        "Product Id": item.productId,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");

      const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(
          new Blob([blob], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        link.setAttribute("href", url);
        link.setAttribute("download", "products.xlsx");
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error exporting products:", error);
    }
  };

  // Debounce searchQuery with 3 seconds delay
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchTableData = async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        pageIndex: pageIndex + 1,
        pageSize: pageSizeState,
        searchQuery: debouncedSearchQuery,
      });
      if (response.data) {
        setData(response.data.data || response.data || response.totalCount);
        const totalItemCount =
          response.data.totalCount ||
          response.totalCount ||
          response.data.counts?.allCount ||
          0;
        setTotalPages(Math.ceil(totalItemCount / pageSize));
      } else {
        setData([]);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, debouncedSearchQuery,pageSizeState]);

  const table = useReactTable({
    columns,
    data,
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater(table.getState().pagination)
          : updater;
      setPageIndex(newState.pageIndex);
    },
  });

  return (
    <div>
      {/* <div className="table-controls">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div> */}
      <Row className="align-items-center my-md-4 dataTableHeader">
        <Col sm="6" md="6" lg="6" xl>
          <div className="d-flex gap-3 ">
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              // onKeyDown={handleKeyPress}
              data-testing-id="searchInput_PaginatedTable"
            />
          </div>
        </Col>

        {location.pathname === "/manage-products" && (
          <Col sm="6" md="6" lg="6 " xl>
            <div className="d-flex justify-content-end">
              <Button onClick={handleExport} style={{ marginRight: "10px" }}>
                Export
              </Button>
              <Link
                to="/product/add"
                className="btn btn-secondary"
                data-testing-Id="newProductButton_DraftProducts"
              >
                <i className="ri-add-line align-bottom me-1"></i> New Product
              </Link>
            </div>
          </Col>
        )}
        {location.pathname === "/brands" && (
          <Col sm="6" md="6" lg="6 " xl>
            <div className="d-flex justify-content-end">
              <Link
                to="/brand-form"
                className="btn btn-secondary Apply-btn"
                data-testing-Id="newApplyBrandButton_DraftProducts"
              >
                <i className="ri-add-line align-bottom me-1"></i> Apply for a
                new brand
              </Link>
            </div>
          </Col>
        )}

        {location.pathname === "/draft-products" && (
          <Col sm="6" md="6" lg="6 " xl>
            <div className="d-flex justify-content-end">
              <Link
                to="/draft/add"
                className="btn btn-secondary"
                data-testing-Id="newProductButton_DraftProducts"
              >
                <i className="ri-add-line align-bottom me-1"></i> New Product
              </Link>
            </div>
          </Col>
        )}
      </Row>

      <div className={divClass}>
        <Table className={tableClass}>
          <thead className={theadClass}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={thClass}>
                    {header.isPlaceholder
                      ? null
                      : typeof header.column.columnDef.header === "function"
                        ? header.column.columnDef.header(header.getContext())
                        : header.column.columnDef.header}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="loading">
                  Loading...
                </td>
              </tr>
            ) : data.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {typeof cell.column.columnDef.cell === "function"
                        ? cell.column.columnDef.cell(cell.getContext())
                        : cell.getValue()}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* <div className="pagination-controls">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Previous
        </button>
        <span>
          Page {pageIndex + 1} of {totalPages}
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next
        </button>
      </div> */}

      <div className="col-sm-auto">
        <ul className="pagination pagination-separated pagination-rounded pagination-md justify-content-center justify-content-sm-start align-items-center mb-0">
          {/* Previous Button */}
          <li
            className={
              !table.getCanPreviousPage() ? "page-item disabled" : "page-item"
            }
          >
            <Link to="#" className="page-link" onClick={table.previousPage}>
              <i
                className="bx bx-left-arrow-alt fs-2"
                style={{ color: "#A2B1C3" }}
              ></i>
            </Link>
          </li>

          {/* First Page Button */}
          {table.getState().pagination.pageIndex > 2 && (
            <li className="page-item">
              <Link
                to="#"
                className="page-link"
                onClick={() => table.setPageIndex(0)}
              >
                1
              </Link>
            </li>
          )}

          {/* Ellipsis Before Current Page Group */}
          {table.getState().pagination.pageIndex > 3 && (
            <li className="page-item disabled">
              <span className="page-link">...</span>
            </li>
          )}

          {/* Page Numbers */}
          {/* {table
      .getPageOptions()
      .slice(
        Math.max(0, table.getState().pagination.pageIndex - 2),
        Math.min(table.getState().pagination.pageIndex + 3, table.getPageCount())
      )
      .map((item: any) => (
        <li key={item} className="page-item">
          <Link
            to="#"
            className={table.getState().pagination.pageIndex === item ? "page-link active" : "page-link"}
            onClick={() => table.setPageIndex(item)}
          >
            {item + 1}
          </Link>
        </li>
      ))} */}
          {table
            .getPageOptions()
            .slice(
              Math.max(0, table.getState().pagination.pageIndex - 2),
              Math.min(
                table.getState().pagination.pageIndex + 3,
                table.getPageCount()
              )
            )
            .map((item: any) => (
              <li key={item} className="page-item">
                <Link
                  to="#"
                  className={
                    table.getState().pagination.pageIndex === item
                      ? "page-link active"
                      : "page-link"
                  }
                  onClick={() => table.setPageIndex(item)}
                >
                  {item + 1}
                </Link>
              </li>
            ))}

          {/* Ellipsis After Current Page Group */}
          {table.getState().pagination.pageIndex < table.getPageCount() - 4 && (
            <li className="page-item disabled">
              <span className="page-link">...</span>
            </li>
          )}

          {/* Last Page Button */}
          {table.getState().pagination.pageIndex < table.getPageCount() - 3 && (
            <li className="page-item">
              <Link
                to="#"
                className="page-link"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              >
                {table.getPageCount()}
              </Link>
            </li>
          )}

          {/* Next Button */}
          <li
            className={
              !table.getCanNextPage() ? "page-item disabled" : "page-item"
            }
          >
            <Link to="#" className="page-link" onClick={table.nextPage}>
              <i
                className="bx bx-right-arrow-alt fs-2 cursor-pointer"
                style={{ color: "#A2B1C3" }}
              ></i>
            </Link>
          </li>
          <Col xs="2 mb-2">
        <div className="form-group">
                 <select
  className="form-select"
  value={pageSizeState}
  onChange={(e) => {
    const newSize = Number(e.target.value);
    setPageSizeState(newSize);
    table.setPageSize(newSize);
    table.setPageIndex(0);
  }}
>
  {[20, 50, 80, 100].map((size) => (
    <option key={size} value={size}>
      Show {size}
    </option>
  ))}
</select>
        </div>
      </Col>
        <Col xs="2 mb-2" style={{marginLeft:"10px"}}>
      <select
  className="form-select"
  value={table.getState().pagination.pageIndex + 1}
  onChange={(e) => {
    const selectedPage = Number(e.target.value) - 1;
    table.setPageIndex(selectedPage);
  }}
>
  {Array.from({ length: totalPages }, (_, i) => (
    <option key={i} value={i + 1}>
      Page {i + 1}
    </option>
  ))}
</select>



          </Col>
         
        </ul>
      </div>
    </div>
  );
};

export default PaginatedTable;
